import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import type { OnboardingStepId } from "@/hooks/onboarding/useOnboardingProgress";

interface OnboardingContextValue {
  tourActive: boolean;
  tourStepIndex: number;
  startTour: (fromStep?: OnboardingStepId | number) => void;
  stopTour: () => void;
  setStepIndex: (i: number) => void;
  welcomeOpen: boolean;
  openWelcome: () => void;
  closeWelcome: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue>({
  tourActive: false,
  tourStepIndex: 0,
  startTour: () => {},
  stopTour: () => {},
  setStepIndex: () => {},
  welcomeOpen: false,
  openWelcome: () => {},
  closeWelcome: () => {},
});

export const useOnboarding = () => useContext(OnboardingContext);

const STEP_ORDER: OnboardingStepId[] = [
  "profile",
  "upload",
  "invite",
  "social",
  "cashout",
];

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [tourActive, setTourActive] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  const startTour = useCallback((fromStep?: OnboardingStepId | number) => {
    let idx = 0;
    if (typeof fromStep === "number") idx = fromStep;
    else if (typeof fromStep === "string") {
      const found = STEP_ORDER.indexOf(fromStep);
      idx = found >= 0 ? found + 1 : 0; // +1 for intro step
    }
    setTourStepIndex(idx);
    setTourActive(true);
  }, []);

  const stopTour = useCallback(() => setTourActive(false), []);
  const openWelcome = useCallback(() => setWelcomeOpen(true), []);
  const closeWelcome = useCallback(() => setWelcomeOpen(false), []);

  const value = useMemo(
    () => ({
      tourActive,
      tourStepIndex,
      startTour,
      stopTour,
      setStepIndex: setTourStepIndex,
      welcomeOpen,
      openWelcome,
      closeWelcome,
    }),
    [tourActive, tourStepIndex, welcomeOpen, startTour, stopTour, openWelcome, closeWelcome],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}
